import { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import MedicinePicker from '../MedicinePicker';
import type { DrugSchedule, GrnDiscrepancyReason, MedicineSearchResult, NewGrnItemInput } from '../types';

/**
 * One Goods Receipt line being drafted client-side — string state while
 * editing (matching every other numeric-line-item form this session),
 * parsed to NewGrnItemInput on submit. Shared by GoodsReceiptPanel (a
 * standalone receipt), PurchaseOrdersPanel's "Mark Received" flow (seeded
 * from a PO's items), and the vendor-invoice review step (seeded from an
 * uploaded invoice's extracted lines) — one line-item form, three entry
 * points, same as this app's other "one engine, multiple front doors"
 * patterns (see utils/pharmacySale.js).
 */
export interface DraftGrnLine {
  medicine_id: number | null;
  resolved_label: string;
  /**
   * Free text to show while `medicine_id` is still null — MedicinePicker's
   * own search box always starts blank (it has no "pre-fill the query"
   * prop), so this is what tells staff what to actually search for: the
   * product name a PO line was raised against, or what AI vision read off
   * an invoice. Ignored once resolved.
   */
  source_hint: string;
  dosage_form: string;
  hsn_code: string;
  gst_percent: string;
  schedule: DrugSchedule;
  batch_number: string;
  expiry_date: string;
  quantity_received: string;
  unit_cost: string;
  mrp: string;
  /** Receiving-time detail — see grn_items' new columns. Blank is fine; a
   * line can still be entered as a flat quantity_received. */
  units_per_pack: string;
  strips_received: string;
  free_strips: string;
  discount_percent: string;
  /** The invoice's own printed line Amount, when known (from an upload, or
   * typed in by hand). Takes precedence over any unit_cost x quantity
   * derivation server-side — see migration_grn_amount_invoice_discount.sql
   * and POST /goods-receipts' baseAmount precedence. Blank is fine; the
   * amount is then computed from paid strips (or quantity) x rate. */
  line_amount: string;
  /** Set when this line came from an invoice upload and the extracted
   * quantity/rate didn't reconcile with the invoice's own printed amount —
   * usually means the Qty/Free columns were misread. Advisory only. */
  mismatch_note?: string;
  /** G12: units on the invoice (auto-filled with the received quantity from
   * strips; differs only when what arrived differs from what was billed). */
  invoiced_quantity: string;
  discrepancy_reason: GrnDiscrepancyReason | '';
  discrepancy_note: string;
  mfg_date: string;
}

export const DISCREPANCY_REASONS: { value: GrnDiscrepancyReason; label: string }[] = [
  { value: 'short_supply', label: 'Short supply' },
  { value: 'damaged', label: 'Damaged in transit' },
  { value: 'excess', label: 'Excess received' },
  { value: 'wrong_item', label: 'Wrong item / batch' },
  { value: 'other', label: 'Other' },
];

/** Received minus invoiced, when both are numbers (0 = nothing to explain). */
export function lineDiscrepancy(line: Pick<DraftGrnLine, 'quantity_received' | 'invoiced_quantity'>): number {
  const received = parseInt(line.quantity_received, 10);
  const invoiced = parseInt(line.invoiced_quantity, 10);
  return Number.isNaN(received) || Number.isNaN(invoiced) ? 0 : received - invoiced;
}

/** The one place a draft line becomes the API payload (GRN and PO receive both use it). */
export function toGrnItemInput(i: DraftGrnLine): NewGrnItemInput {
  const diff = lineDiscrepancy(i);
  return {
    medicine_id: i.medicine_id as number,
    generic_name: i.resolved_label,
    dosage_form: i.dosage_form,
    hsn_code: i.hsn_code || undefined,
    gst_percent: parseFloat(i.gst_percent) || 12,
    schedule: i.schedule,
    batch_number: i.batch_number,
    expiry_date: i.expiry_date,
    quantity_received: parseInt(i.quantity_received, 10),
    unit_cost: i.unit_cost ? parseFloat(i.unit_cost) : null,
    mrp: parseFloat(i.mrp),
    units_per_pack: i.units_per_pack ? parseInt(i.units_per_pack, 10) : null,
    strips_received: i.strips_received ? parseInt(i.strips_received, 10) : null,
    free_strips: i.free_strips ? parseInt(i.free_strips, 10) : undefined,
    discount_percent: i.discount_percent ? parseFloat(i.discount_percent) : undefined,
    line_amount: i.line_amount ? parseFloat(i.line_amount) : undefined,
    invoiced_quantity: i.invoiced_quantity ? parseInt(i.invoiced_quantity, 10) : null,
    discrepancy_reason: diff !== 0 && i.discrepancy_reason ? i.discrepancy_reason : null,
    discrepancy_note: diff !== 0 && i.discrepancy_note.trim() ? i.discrepancy_note.trim() : null,
    mfg_date: i.mfg_date || null,
  };
}

function defaultGst(gstPercentageOptions: number[]): string {
  return gstPercentageOptions.includes(12) ? '12' : String(gstPercentageOptions[0] ?? 0);
}

// Mirrors backend/utils/vendorInvoiceExtraction.js's reconcileLine() exactly
// (same tolerance, same two conventions for whether the invoice's Amount
// already includes GST) — kept in sync deliberately so a line that reads as
// mismatched server-side reads the same way here, and so editing a line
// after upload re-runs the identical check instead of a looser client-only
// approximation.
const RECONCILE_TOLERANCE = 0.12;

/** Re-checks paid strips × rate against the invoice's own printed Amount
 * whenever a reconcile-relevant field changes, clearing (or setting) the
 * mismatch warning to match — a line flagged from an upload stops warning
 * once the user corrects the Qty/Free/rate/amount that caused it, rather
 * than showing a stale warning forever. Nothing to compare against (any of
 * quantity/rate/amount missing or amount blank/zero) leaves whatever flag
 * already existed untouched — same "can't disprove it" behavior as the
 * backend's own reconcileLine(). */
function reconcileDraftLine(line: DraftGrnLine): DraftGrnLine {
  const quantity = parseFloat(line.strips_received);
  const rate = parseFloat(line.unit_cost);
  const amount = parseFloat(line.line_amount);
  if (!Number.isFinite(quantity) || !Number.isFinite(rate) || !Number.isFinite(amount) || amount === 0) return line;
  const discount = parseFloat(line.discount_percent) || 0;
  const gst = parseFloat(line.gst_percent) || 0;
  const net = quantity * rate * (1 - discount / 100);
  const withGst = net * (1 + gst / 100);
  const closeEnough = (a: number, b: number) => Math.abs(a - b) / Math.max(Math.abs(b), 1) <= RECONCILE_TOLERANCE;
  const reconciles = closeEnough(net, amount) || closeEnough(withGst, amount);
  if (reconciles) return line.mismatch_note ? { ...line, mismatch_note: undefined } : line;
  return {
    ...line,
    mismatch_note: `quantity × rate (₹${net.toFixed(2)}) doesn't match the invoice's printed amount (₹${amount}) — double-check the Qty/Free columns for this row.`,
  };
}

export function emptyGrnLine(gstPercentageOptions: number[]): DraftGrnLine {
  return {
    medicine_id: null, resolved_label: '', source_hint: '', dosage_form: 'Tablet', hsn_code: '',
    gst_percent: defaultGst(gstPercentageOptions), schedule: 'OTC',
    batch_number: '', expiry_date: '', quantity_received: '', unit_cost: '', mrp: '',
    units_per_pack: '', strips_received: '', free_strips: '', discount_percent: '', line_amount: '',
    invoiced_quantity: '', discrepancy_reason: '', discrepancy_note: '', mfg_date: '',
  };
}

interface GrnLineEditorProps {
  items: DraftGrnLine[];
  onChange: (items: DraftGrnLine[]) => void;
  gstPercentageOptions: number[];
}

/** G12: manufacturing date, invoice quantity, and why what arrived differs from the invoice. */
function DiscrepancyRow({ line, onChange }: { line: DraftGrnLine; onChange: (patch: Partial<DraftGrnLine>) => void }) {
  const diff = lineDiscrepancy(line);
  return (
    <div className="mt-1.5 grid grid-cols-[1fr_1fr_2.6fr] items-start gap-1.5">
      <label className="flex flex-col text-[10px] font-semibold text-ink-muted">
        Mfg date
        <Input type="date" value={line.mfg_date} onChange={(e) => onChange({ mfg_date: e.target.value })} className="text-xs" />
      </label>
      <label className="flex flex-col text-[10px] font-semibold text-ink-muted">
        Invoice qty (units)
        <Input type="number" value={line.invoiced_quantity} placeholder="as received"
          onChange={(e) => onChange({ invoiced_quantity: e.target.value })} className="text-xs"
          title="Units on the supplier's invoice, paid + free. Leave blank if everything billed arrived." />
      </label>
      {diff !== 0 ? (
        <div className="rounded-[var(--radius)] bg-warn-soft px-2 py-1 text-[11px] text-warn-ink">
          <div className="mb-1 font-semibold">
            {Math.abs(diff)} unit(s) {diff < 0 ? 'fewer' : 'more'} than the invoice. Stock goes up by what was received.
          </div>
          <div className="grid grid-cols-[1fr_1.4fr] gap-1.5">
            <NativeSelect value={line.discrepancy_reason} onChange={(e) => onChange({ discrepancy_reason: e.target.value as GrnDiscrepancyReason })}
              className="text-xs" aria-label="Reason for the difference">
              <option value="">Reason…</option>
              {DISCREPANCY_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </NativeSelect>
            <Input value={line.discrepancy_note} onChange={(e) => onChange({ discrepancy_note: e.target.value })}
              placeholder={line.discrepancy_reason === 'other' ? 'Describe the difference *' : 'Note (optional)'} className="text-xs" />
          </div>
        </div>
      ) : <div />}
    </div>
  );
}

export default function GrnLineEditor({ items, onChange, gstPercentageOptions }: GrnLineEditorProps) {
  // If a line's GST% (12 by default) isn't one of the admin's configured
  // percentages, fall back to the first configured option rather than
  // leaving a <select> whose value matches no <option>.
  useEffect(() => {
    const options = gstPercentageOptions.map(String);
    if (!options.length) return;
    const next = items.map((it) => (options.includes(it.gst_percent) ? it : { ...it, gst_percent: options[0] as string }));
    if (next.some((it, i) => it !== items[i])) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstPercentageOptions]);

  const updateItem = (idx: number, patch: Partial<DraftGrnLine>) => {
    onChange(items.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, ...patch };
      // Strip-based receiving is a convenience over quantity_received, not a
      // second source of truth — auto-fill it from strips×units-per-pack
      // whenever either strip field or the pack size changes, but leave
      // quantity_received itself a plain editable input so a direct
      // override (or a line entered without strips at all) still works.
      if ('strips_received' in patch || 'free_strips' in patch || 'units_per_pack' in patch) {
        const strips = parseInt(next.strips_received, 10);
        const free = parseInt(next.free_strips, 10) || 0;
        const perPack = parseInt(next.units_per_pack, 10);
        // Strips on the invoice set both: what was billed, and (until someone
        // counts differently) what arrived.
        if (!Number.isNaN(strips) && !Number.isNaN(perPack)) {
          next.quantity_received = String((strips + free) * perPack);
          next.invoiced_quantity = next.quantity_received;
        }
      }
      if ('strips_received' in patch || 'unit_cost' in patch || 'line_amount' in patch || 'discount_percent' in patch || 'gst_percent' in patch) {
        return reconcileDraftLine(next);
      }
      return next;
    }));
  };

  const removeLine = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const addLine = () => onChange([...items, emptyGrnLine(gstPercentageOptions)]);

  return (
    <div>
      {items.map((it, idx) => (
        <div key={idx} className={`mb-2 rounded-[var(--radius)] border p-2.5 ${it.mismatch_note ? 'border-warn/40 bg-warn-soft' : 'border-line bg-surface'}`}>
          {it.mismatch_note && (
            <div className="mb-1.5 text-[11px] font-medium text-warn-ink">⚠ Check this line: {it.mismatch_note}</div>
          )}
          <div className="mb-1.5 flex items-start gap-1.5">
            <div className="flex-1">
              {!it.medicine_id && it.source_hint && (
                <div className="mb-1 truncate text-[10.5px] text-ink-muted" title={it.source_hint}>From invoice: {it.source_hint}</div>
              )}
              <div className="grid grid-cols-[1fr_90px] gap-1.5">
                <MedicinePicker
                  medicineId={it.medicine_id}
                  label={it.resolved_label}
                  onResolved={(r: MedicineSearchResult) => updateItem(idx, {
                    medicine_id: r.id,
                    resolved_label: `${r.generic_name}${r.brand_name ? ` — ${r.brand_name}` : ''}${r.strength ? ` ${r.strength}` : ''}`,
                    dosage_form: r.dosage_form || it.dosage_form,
                    // G10: the product's usual pack size, unless this line already has one.
                    ...(r.pack_size && !it.units_per_pack ? { units_per_pack: String(r.pack_size) } : {}),
                  })}
                  onClear={() => updateItem(idx, { medicine_id: null, resolved_label: '' })}
                />
                <NativeSelect value={it.schedule} onChange={(e) => updateItem(idx, { schedule: e.target.value as DrugSchedule })} className="text-xs">
                  {(['OTC', 'H', 'H1', 'X'] as const).map((s) => <option key={s} value={s}>{s}</option>)}
                </NativeSelect>
              </div>
            </div>
            {items.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} title="Remove this line">✕</Button>
            )}
          </div>
          <div className="mb-1.5 grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1.6fr] gap-1.5">
            <Input placeholder="Batch *" value={it.batch_number} onChange={(e) => updateItem(idx, { batch_number: e.target.value })} className="text-xs" />
            <Input type="date" value={it.expiry_date} onChange={(e) => updateItem(idx, { expiry_date: e.target.value })} className="text-xs" title="Expiry date" />
            <Input type="number" placeholder="Qty (units) *" value={it.quantity_received} onChange={(e) => updateItem(idx, { quantity_received: e.target.value })} className="text-xs" title="Total units added to stock — auto-filled from Strips × Units/Strip below, or type it directly" />
            <Input type="number" placeholder="Rate *" value={it.unit_cost} onChange={(e) => updateItem(idx, { unit_cost: e.target.value })} className="text-xs" title="Purchase rate per strip (or per unit, if entered without strips below), before discount" />
            <Input type="number" placeholder="MRP *" value={it.mrp} onChange={(e) => updateItem(idx, { mrp: e.target.value })} className="text-xs" />
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-semibold text-ink-muted">GST</span>
              <NativeSelect value={it.gst_percent} onChange={(e) => updateItem(idx, { gst_percent: e.target.value })} className="text-xs" title="GST % on this medicine's purchase cost">
                {gstPercentageOptions.map((g) => <option key={g} value={g}>{g}%</option>)}
              </NativeSelect>
            </div>
          </div>
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1.4fr] gap-1.5">
            <Input type="number" placeholder="No. of strips" value={it.strips_received} onChange={(e) => updateItem(idx, { strips_received: e.target.value })} className="text-xs" title="Strips/packs billed by the vendor (paid quantity — excludes free)" />
            <Input type="number" placeholder="Free strips" value={it.free_strips} onChange={(e) => updateItem(idx, { free_strips: e.target.value })} className="text-xs" title="Free strips/packs received, if any — adds to stock but not to the amount" />
            <Input type="number" placeholder="Units/strip" value={it.units_per_pack} onChange={(e) => updateItem(idx, { units_per_pack: e.target.value })} className="text-xs" title="Units per strip/pack (e.g. 10, 15)" />
            <Input type="number" placeholder="Discount %" value={it.discount_percent} onChange={(e) => updateItem(idx, { discount_percent: e.target.value })} className="text-xs" title="Discount off the vendor's rate, applied before GST" />
            <Input type="number" placeholder="Amount ₹" value={it.line_amount} onChange={(e) => updateItem(idx, { line_amount: e.target.value })} className="text-xs" title="This line's total as printed on the invoice — paid strips × rate, never including free stock. Leave blank to compute it automatically." />
            <Input placeholder="HSN code" value={it.hsn_code} onChange={(e) => updateItem(idx, { hsn_code: e.target.value })} className="text-xs" />
          </div>
          <DiscrepancyRow line={it} onChange={(p) => updateItem(idx, p)} />
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addLine}>+ Add line</Button>
    </div>
  );
}
